/**
 * Message types for PostMessage communication between iframe and parent window
 */
export const MESSAGE_TYPES = {
  // Handshake messages
  READY: 'READY',                          // iframe → host: Iframe loaded and ready
  HANDSHAKE: 'HANDSHAKE',                  // host → iframe: Host origin identification
  HANDSHAKE_ACK: 'HANDSHAKE_ACK',          // iframe → host: Origin validated, connection established

  // Unified Action Protocol (new)
  ACTION_REQUEST: 'ACTION_REQUEST',        // host → iframe: Unified action request
  URL_REDIRECT: 'URL_REDIRECT',            // iframe → host: Unified navigation request

  // Account query messages (legacy - kept for backward compatibility)
  GET_ACCOUNTS: 'GET_ACCOUNTS',            // host → iframe: Request account list
  ACCOUNTS_DATA: 'ACCOUNTS_DATA',          // iframe → host: Send account data

  // Account action messages (legacy - kept for backward compatibility)
  SWITCH_ACCOUNT: 'SWITCH_ACCOUNT',        // host → iframe: Request account switch
  ACCOUNT_SWITCHED: 'ACCOUNT_SWITCHED',    // iframe → host: Account switch completed
  ADD_ACCOUNT: 'ADD_ACCOUNT',              // host → iframe: Request add account flow
  ACCOUNT_ADDED: 'ACCOUNT_ADDED',          // iframe → host: Account added successfully

  // Cross-product handoff (Sharing protocol — F3/F4/A6)
  // Values MUST be SCREAMING_SNAKE to match the auth-service iframe's own
  // MESSAGE_TYPES registry (resources/js/widgets/message-protocol.js). The
  // iframe's isValidMessage() whitelists by value, so a kebab-case slug is
  // silently dropped and the handoff never completes.
  ADD_ACCOUNT_WITH_TOKEN: 'ADD_ACCOUNT_WITH_TOKEN',                  // host → iframe: push a pre-authenticated account
  ADD_ACCOUNT_WITH_TOKEN_OK: 'ADD_ACCOUNT_WITH_TOKEN_OK',            // iframe → host: cookie written, account appended
  ADD_ACCOUNT_WITH_TOKEN_FAILED: 'ADD_ACCOUNT_WITH_TOKEN_FAILED',    // iframe → host: backend rejected the session token

  // Prep–Sign–Promote pattern (v1.4 sharing/prep/*)
  PREP_SIGNED:      'prep-signed',         // peer iframe → host: signature captured, prep flipped to 'signed'
  PREP_SIGN_FAILED: 'prep-sign-failed',    // peer iframe → host: signature rejected by intent handler

  MANAGE_ACCOUNT: 'MANAGE_ACCOUNT',        // iframe → host: Request account management flow
  UPDATE_AVATAR: 'UPDATE_AVATAR',          // iframe → host: Request avatar update flow
  LOGOUT_ACCOUNT: 'LOGOUT_ACCOUNT',        // host → iframe: Request logout specific account
  ACCOUNT_LOGGED_OUT: 'ACCOUNT_LOGGED_OUT', // iframe → host: Account logged out
  LOGOUT_ALL: 'LOGOUT_ALL',                // host → iframe: Request logout all accounts
  ALL_LOGGED_OUT: 'ALL_LOGGED_OUT',        // iframe → host: All accounts logged out

  // Interactive request messages (iframe → host)
  CONFIRM_REQUEST: 'CONFIRM_REQUEST',      // iframe → host: Request user confirmation
  PROMPT_REQUEST: 'PROMPT_REQUEST',        // iframe → host: Request user text input
  ALERT_REQUEST: 'ALERT_REQUEST',          // iframe → host: Request user acknowledgment
  REQUEST_RESPONSE: 'REQUEST_RESPONSE',    // host → iframe: Response to interactive request

  // Event notifications
  SESSION_CHANGED: 'SESSION_CHANGED',      // iframe → host: Session state changed
  TOKEN_REFRESHED: 'TOKEN_REFRESHED',      // iframe → host: Auth token refreshed
  SIZE_CHANGED: 'SIZE_CHANGED',            // iframe → host: Content height changed

  // External session management
  SET_SESSION: 'SET_SESSION',              // host → iframe: Set session from external login
  SESSION_SET: 'SESSION_SET',              // iframe → host: Confirm session was set

  // Page context messages
  SET_PAGE_URL: 'SET_PAGE_URL',            // host → iframe: Update current page URL

  // Error messages
  ERROR: 'ERROR',                          // both ways: Error notification
  UNAUTHORIZED: 'UNAUTHORIZED',            // iframe → host: Origin not authorized
} as const;

/**
 * PostMessage message structure
 */
export interface PostMessageData {
  type: string;
  payload?: any;
  requestId?: string;
  timestamp?: number;
  version?: string;
}

/**
 * Session change event payload
 */
export interface SessionChangedPayload {
  isAuthenticated: boolean;
  currentUser: any;
  accounts: any[];
}

/**
 * Account switched event payload
 */
export interface AccountSwitchedPayload {
  uuid: string;
  name: string;
  email: string;
  avatar_url?: string;
}

/**
 * Error event payload
 */
export interface ErrorPayload {
  message: string;
  code?: string;
  details?: any;
}

/**
 * Resize event payload
 */
export interface ResizePayload {
  height: number;
}

/**
 * Action request payload (Unified Action Protocol)
 */
export interface ActionRequestPayload {
  action: string;
  params: Record<string, any>;
}

/**
 * URL redirect payload (Unified Action Protocol)
 */
export interface UrlRedirectPayload {
  url: string;
  reason?: string;
  openInNewTab?: boolean;
}

/**
 * Sharing protocol — request payload sent by `multiAccountAutoAdd` (F4) to the
 * auth-service iframe (A6). The iframe redeems the session token at its own
 * backend, writes the destination-scoped session cookie, and acks with
 * ADD_ACCOUNT_WITH_TOKEN_OK (or ..._FAILED).
 */
export interface AddAccountWithTokenPayload {
  /** UUID of the user being added — must match the user the session token was issued for. */
  accountUuid: string;
  /** Short-lived session token returned by the handoff exchange (F1). */
  sessionToken: string;
}

/**
 * Sharing protocol — iframe → host: cookie written and account appended.
 */
export interface AddAccountWithTokenOkPayload {
  accountUuid: string;
}

/**
 * Sharing protocol — iframe → host: redemption failed.
 *
 * `reason` is one of:
 * - `http_401` — session token invalid / expired (auth-service rejected)
 * - `http_4xx` / `http_5xx` — generic upstream error
 * - `network` — fetch to iframe's own backend failed
 * - `mismatch` — session token's user didn't match `accountUuid`
 */
export interface AddAccountWithTokenFailedPayload {
  reason: string;
  message?: string;
}

/**
 * Prep–Sign–Promote (v1.4) — iframe → host: signature captured.
 * Fires from the peer's /sharing/embed/{slug}/{prep_id} surface after a
 * successful POST to .../submit. The orchestrator's <PrepEmbed> listens
 * for this to chain into Sharing::shareUser + Sharing::promote.
 */
export interface PrepSignedPayload {
  prep_id: string;
}

/**
 * Prep–Sign–Promote (v1.4) — iframe → host: signature rejected.
 * `error` mirrors the JSON body's `error` field from /submit (e.g.
 * `handler_rejected`, `invalid_state`, `not_found`).
 */
export interface PrepSignFailedPayload {
  prep_id: string;
  error: string;
  message?: string;
}
